import { InfoCircleTwoTone } from '@ant-design/icons';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  Typography,
  Modal,
  Space,
  Card,
} from 'antd';
import Clipboard, { Event } from 'clipboard';
import React, { useState, useEffect, Fragment } from 'react';
import IconCopy from '@/assets/images/public/icon-copy.svg';
import { MultiApp } from '@/config/context';
import { getUserId, isInternal } from '@/infrastructure/auth';
import { BASE_NAME } from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { getPid, getProductId } from '@/infrastructure/product-info';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { useReportPage } from '@/infrastructure/reporter/datong/report';
import { AppSlot } from '@/presentation/components/app-slot';
import { hintText } from '@/presentation/components/hint-text/hintText';
import { Toggle } from '@/presentation/components/toggle/toggle';
import useToggle from '@/presentation/components/toggle/use-toggle';
import { getRoleByUser } from '@/presentation/components/user-manage/api';
import { OrgType } from '@/proto/manage';
import { IUpdateProductOaReq, productOAServer } from '@/service/productoa';
import ProductInfoPro from '../info-pro';
import {
  getEpcBizList,
  getEpcSubBizList,
  deleteApp,
  getAppConfigInfo,
  updateAppInfo,
} from './api';
import style from './style.module.scss';

const { TextArea } = Input;
const { Text } = Typography;

const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 20 },
};

export interface DeleteAppReq {
  appId: string;
  pid: string;
  userId: string;
}

enum AppFormMap {
  '-' = 0,
  'APP' = 1,
  'SDK' = 2,
  'Extension' = 3,
  '跨端产品' = 4
}

export enum OrgTypeName {
  '未知'=OrgType.UNKNOWN_ORG,
  '公司'=OrgType.COMPANY,
  '组织'=OrgType.ORGANIZATION,
  '个人'=OrgType.INDIVIDUAL
}

enum AppPlatformMap {
  '未知' = -1,
  'Android' = 1,
  'iOS' = 2,
  'Mac' = 4,
  'Flutter' = 9,
  'Windows' = 10,
  'Linux' = 5,
  'Harmony' = 11,
  'VisionOS' = 17,
}

enum AppTypeMap {
  '-' = 0,
  '办公商务' = 3,
  '聊天通讯' = 4,
  '系统输入' = 5,
  '手机美化' = 6,
  '阅读学习' = 7,
  '出行地图' = 8,
  '影音视听' = 9,
  '儿童亲子' = 10,
  '拍摄美化' = 11,
  '购物时尚' = 12,
  '生活娱乐' = 13,
  '实用工具' = 14,
  '动作冒险' = 15,
  '飞行射击' = 16,
  '角色扮演' = 17,
  '经营策略' = 18,
  '体育竞速' = 19,
  '网络游戏' = 20,
  '益智游戏' = 21,
  '休闲娱乐' = 22,
};

export interface UpdateAppInfoReq {
  appId: string;
  desc: string;
  epcBizId: string;
  epcBizName: string;
  epcSubBizId: string;
  epcSubBizName: string;
  logoUrl: string;
  name: string;
  pid: number;
  pkgSign: string;
  type: number;
  userId: string;
}


interface AppInfoType {
  productName: string;
  department: string;
  productId: string;
  productKey: string;
  appForm: number;
  platformId: number;
  type: number;
  creator: string;
  createTime: string;
  description: string;
  pkgSign: string;
  bundleId: string;
  isFlutter: number;
  epc: {
    bizId: string;
    bizName: string;
    subBizId: string;
    subBizName: string;
  };
  diagnoseAppID: string;
  diagnoseAppKey: string;
}

interface OptionsType {
  label: string;
  value: string;
}

const ProductInfo = () => {
  useReportPage(PAGE_ID.setting.productInfo);
  const [productEnable] = useToggle('product', ['user']);
  const [clipboard, setClipboard] = useState<any>(null);
  const [appInfo, setAppInfo] = useState<AppInfoType>();
  const [epcBizListData, setEpcBizListData] = useState<OptionsType[]>();
  const [epcSubBizListData, setEpcSubBizListData] = useState<OptionsType[]>();
  const [isAdmin, setIsAdmin] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    // TODO 接口切换暂时添加白名单
    const toggle = new Toggle();
    const productEnable = await toggle.isEnable('product', ['user'], {
      user: getUserId(),
      appId: getProductId(),
    });
    if (productEnable) {
      const data = await productOAServer.getProductOaInfo({ appId: getProductId() });
      if (Array.isArray(data) || !data) return;
      const info = {
        productName: data?.name,
        department: data?.department,
        productId: data?.appId,
        productKey: data?.appKey,
        appForm: data?.appForm,
        platformId: data?.platformId,
        type: data?.type,
        creator: data?.ownerId,
        createTime: data?.createTime,
        description: data?.description,
        pkgSign: data?.pkgSign,
        epc: data?.epc,
        bundleId: data?.bundleId,
        isFlutter: data?.isFlutter,
        diagnoseAppID: data?.diagnoseAppId,
        diagnoseAppKey: data?.diagnoseAppKey,
      };
      setAppInfo(info);
    } else {
      const data = await getAppConfigInfo({ appId: getProductId() });
      if (Array.isArray(data)) return;
      const info = {
        productName: data.name,
        department: data.department,
        productId: data.appId,
        productKey: data.appKey,
        appForm: data.appForm,
        platformId: data.platformId,
        type: data.type,
        creator: data.ownerId,
        createTime: data.createTime,
        description: data.description,
        pkgSign: data.pkgSign,
        epc: data.epc,
        bundleId: data.related?.bundleId,
        isFlutter: data.isFlutter,
        diagnoseAppID: data.diagnoseAppID,
        diagnoseAppKey: data.diagnoseAppKey,
      };
      setAppInfo(info);
    }

    const isAdmin = await getRoleByUser();
    setIsAdmin(isAdmin);
    const epcBizList = await getEpcBizList();
    const epcBizListData = [
      {
        label: '不关联',
        value: '--',
      },
    ];
    if (epcBizList) {
      epcBizListData.push(...epcBizList.map((item: { bizKey: string, bizName: string }) => ({
        label: item.bizName,
        value: item.bizKey,
      })));
    }
    setEpcBizListData(epcBizListData);
  };

  useEffect(() => {
    load();
    setClipboard(new Clipboard('#copyBtn'));
    return () => clipboard?.destroy();
  }, []);

  useEffect(() => {
    clipboard?.on('success', () => {
      message.destroy();
      message.success('复制成功');
    });
    clipboard?.on('error', (e: Event) => {
      message.destroy();
      message.error('复制失败');
      logger.error('clipboard', e?.trigger, e?.action);
    });
  }, [clipboard]);

  useEffect(() => {
    if (appInfo?.epc?.bizId && !isInternal()) {
      loadEpcSubBiz(appInfo.epc.bizId);
    }
  }, [appInfo]);

  const submitAppInfo = async (params: IUpdateProductOaReq) => {
    const data = await productOAServer.updateProductOa(params);
    if (data) {
      message.success('保存成功');
    }
  };
  const oldSubmitAppInfo = async (params: UpdateAppInfoReq) => {
    const data = await updateAppInfo(params);
    if (data.code === 200) {
      message.success('保存成功');
    }
  };
  const onFinish = (values: any) => {
    if (!appInfo) return;

    // TODO 接口切换暂时添加白名单
    if (productEnable) {
      let params: IUpdateProductOaReq = {
        appId: appInfo.productId,
        desc: values.description,
        name: appInfo.productName,
        platformId: appInfo.platformId,
        sign: appInfo.pkgSign,
        type: appInfo.type,
        userId: getUserId(),
        bizId: appInfo?.epc?.bizId || '',
        bizName: appInfo?.epc?.bizName || '',
        subBizId: appInfo?.epc?.subBizId || '',
        subBizName: appInfo?.epc?.subBizName || '',
      };
      if (values?.epc?.bizId && values?.epc?.bizId !== '--') {
        if (!values?.epc?.subBizId) {
          return;
        }
        const bizName = epcBizListData?.filter(item => item.value === values?.epc?.bizId)?.[0].label;
        const subBizName = epcSubBizListData?.filter(item => item.value === values.epc.subBizId)?.[0].label;
        params = {
          ...params,
          bizId: values.epc.bizId,
          bizName: bizName || '',
          subBizId: values.epc.subBizId,
          subBizName: subBizName || '',
        };
      }
      submitAppInfo(params);
    } else {
      let params = {
        appId: appInfo.productId,
        desc: values.description,
        logoUrl: '',
        name: appInfo.productName,
        pid: appInfo.platformId,
        pkgSign: values.pkgSign,
        type: appInfo.type,
        userId: getUserId(),
        epcBizId: '',
        epcBizName: '',
        epcSubBizId: '',
        epcSubBizName: '',
      };
      if (values?.epc?.bizId && values?.epc?.bizId !== '--') {
        if (!values.epc.subBizId) {
          return;
        }
        const bizName = epcBizListData?.filter(item => item.value === values.epc.bizId)?.[0].label;
        const subBizName = epcSubBizListData?.filter(item => item.value === values.epc.subBizId)?.[0].label;
        params = {
          ...params,
          epcBizId: values.epc.bizId,
          epcBizName: bizName || '',
          epcSubBizId: values.epc.subBizId,
          epcSubBizName: subBizName || '',
        };
      }
      oldSubmitAppInfo(params);
    }
  };
  const loadEpcSubBiz = async (value: string) => {
    const epcSubBizList = await getEpcSubBizList({ id: value });
    let epcSubBizListData = [];
    if (epcSubBizList.subBizList) {
      epcSubBizListData = epcSubBizList.subBizList
        .map((item: { productId: string, productName: string }) => ({
          label: item.productName,
          value: item.productId,
        }));
    }
    setEpcSubBizListData(epcSubBizListData);
  };

  const onDelete = async () => {
    Modal.confirm({
      title: `删除产品${appInfo?.productName}`,
      icon: <InfoCircleTwoTone />,
      content: '已清楚了解删除产品产生的影响，并确定要删除产品？',
      onOk: async () => {
        const pid = getPid();
        if (productEnable) {
          const rsp = await productOAServer.deleteProductOa({
            appId: getProductId(),
            platformId: pid ? Number(pid) : undefined,
            userId: getUserId(),
          });
          if (!rsp) {
            message.success('删除失败');
            return;
          }
        } else {
          await deleteApp({
            appId: getProductId(),
            pid,
            userId: getUserId(),
          });
        }

        message.success('删除产品成功');
        location.href = `${BASE_NAME}/workbench/apps`;
      },
    });
  };

  const renderPlatform = () => {
    if (appInfo?.isFlutter === 1) return 'Flutter';

    return AppPlatformMap[appInfo?.platformId || -1] || '其他';
  };

  return <Card bordered={false}>
    <div className={style.productInfoContainer}>
      <Form
        {...layout}
        style={{ width: '100%' }}
        form={form}
        onFinish={onFinish}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="产品名称">
              <span>{appInfo?.productName}</span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="所属BG/部门" >
              <span className={style.department}>{appInfo?.department && appInfo.department !== '0' ? appInfo.department : '-' }</span>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="APP ID" >
              <span id="appId">{appInfo?.productId}</span>
              <img
                id="copyBtn"
                style={{ paddingLeft: '8px', cursor: 'pointer' }}
                title={'复制APPID'}
                src={IconCopy}
                alt='Copy to clipboard'
                data-clipboard-target="#appId" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="APP KEY" >
              <span id="appKey">{appInfo?.productKey}</span>
              <img
                id="copyBtn"
                style={{ paddingLeft: '8px', cursor: 'pointer' }}
                title={'复制APPKEY'}
                src={IconCopy}
                alt='Copy to clipboard'
                data-clipboard-target="#appKey" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          {appInfo?.diagnoseAppID ? <Col span={12}>
            <Form.Item label={hintText('日志 APP ID', '2024年4月1日前，在日志独立Portal创建的产品有独立的APP ID和APP KEY。之后创建的产品，采用统一的APP ID和APP KEY。')} >
              <span id="diagnoseAppId">{appInfo?.diagnoseAppID || '-'}</span>
              {appInfo?.diagnoseAppID && <img
                id="copyBtn"
                style={{ paddingLeft: '8px', cursor: 'pointer' }}
                title={'复制APPID'}
                src={IconCopy}
                alt='Copy to clipboard'
                data-clipboard-target="#diagnoseAppId"/>}
            </Form.Item>
          </Col> : null}
          {appInfo?.diagnoseAppKey ? <Col span={12}>
            <Form.Item label="日志 APP KEY">
              <span id="diagnoseAppKey">{appInfo?.diagnoseAppKey || '-'}</span>
              {appInfo?.diagnoseAppKey && <img
                id="copyBtn"
                style={{ paddingLeft: '8px', cursor: 'pointer' }}
                title={'复制APPKEY'}
                src={IconCopy}
                alt='Copy to clipboard'
                data-clipboard-target="#diagnoseAppKey"/>}
            </Form.Item>
          </Col> : null}
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Bundle ID">
              <span id="bundleId">{appInfo?.bundleId}</span>
              <img
                id="copyBtn"
                style={{ paddingLeft: '8px', cursor: 'pointer' }}
                title={'复制BundleID'}
                src={IconCopy}
                alt='Copy to clipboard'
                data-clipboard-target="#bundleId" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="产品形态" >
              <span>{AppFormMap[appInfo?.appForm || 0]}</span>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="平台" >
              <span>{renderPlatform()}</span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="产品类型" >
              <span>{AppTypeMap[appInfo?.type || 0]}</span>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="创建人" >
              <span>{appInfo?.creator}</span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="创建时间" >
              <span>{appInfo?.createTime}</span>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          labelCol={{ span: 2 }}
          wrapperCol={{ span: 22 }}
          name="description"
          label="产品描述"
          className={style.formItem}
          key={appInfo?.description}
          initialValue={appInfo?.description}
        >
          <TextArea
            maxLength={500}
            showCount={true}
            placeholder={'请输入内容'}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => form.setFieldsValue({
              description: event.target.value,
            })}
          />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 2, span: 22 }}>
          <Button disabled={!isAdmin} style={{ marginRight: '0' }} type="primary" htmlType="submit">
            保存
          </Button>
        </Form.Item>
        <div style={{ padding: '30px 0', textAlign: 'right' }}>
          <Space size={22}>
            <Text type="warning">
              点击删除产品后，该产品的所有数据将被永久移除，无法再查看或下载。请注意，此操作不可撤销。
                </Text>
            <Button style={{ background: '#EDF1F7', border: 'none' }} onClick={onDelete} disabled={!isAdmin}>删除产品</Button>
          </Space>
        </div>
      </Form>
    </div>
  </Card>;
};

export default () => (
    <Fragment>
      <AppSlot apps={[MultiApp.WOA, MultiApp.Internal]}>
        <ProductInfo />
      </AppSlot>
      <AppSlot apps={[MultiApp.Pro, MultiApp.Sgp]}>
        <ProductInfoPro />
      </AppSlot>
    </Fragment>
);
